// <emma-chat-typing-prompt> — animated rotating placeholder for the
// main input textarea.
//
// Behavior (port from monolith static/index.html:2520+, audit §8.1):
//   - Cycles through `prompts` strings
//   - Types each char with setTimeout 35ms + random jitter up to 25ms
//   - After full type: hold 3000ms
//   - Clears at 3 chars per 15ms
//   - 300ms pause, then next prompt
//   - Aborted cleanly on `paused=true` or component disconnect
//
// Important: this component MUTATES the `target` textarea's value
// directly (not via Lit reactivity) — the textarea is owned by the
// parent <emma-chat-input> and the typing engine just animates text
// into it. This is the same pattern as the monolith — keeps the
// textarea as the source of truth for the user's actual text.
//
// When user focuses or types in the textarea, parent should set
// `paused=true` to halt the rotation (replicates the monolith's
// `completeTyping` behavior).

import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { designTokens } from './design-tokens.js';

const TYPE_BASE_MS = 35;
const TYPE_JITTER_MS = 25;
const HOLD_MS = 3000;
const CLEAR_STEP_CHARS = 3;
const CLEAR_STEP_MS = 15;
const PAUSE_AFTER_CLEAR_MS = 300;

@customElement('emma-chat-typing-prompt')
export class EmmaChatTypingPrompt extends LitElement {
  /** List of strings to rotate through. */
  @property({ type: Array })
  prompts: string[] = [];

  /** When true, rotation is halted (textarea content not modified). */
  @property({ type: Boolean })
  paused = false;

  /** Target textarea. The component types text into target.value. */
  @property({ attribute: false })
  target: HTMLTextAreaElement | null = null;

  // Component-local rotation state
  private timer: number | null = null;
  private currentIndex = 0;
  private aborted = false;
  /** Full string of the prompt currently being typed (or just-typed but
   *  not yet erased). Parent reads this when intercepting focus to
   *  instantly snap textarea.value to the full prompt without waiting
   *  for the per-char animation to finish. Empty string when no prompt
   *  is active (between rotations / before start / after disconnect). */
  private _currentTarget = '';

  /** Public — full text of the currently-active prompt. Stable for the
   *  lifetime of one typeText() call (set on entry, cleared on the next
   *  rotation iteration after erase). */
  get currentTarget(): string {
    return this._currentTarget;
  }

  static override styles = [
    designTokens,
    css`
      :host {
        display: contents;
      }
    `,
  ];

  override connectedCallback(): void {
    super.connectedCallback();
    // Don't start here — target is typically null on first connect
    // because the parent's @query('textarea') hasn't resolved yet.
    // updated() picks this up on the next render once target lands.
    if (!this.paused && this.target && this.prompts.length > 0) {
      this.startRotation();
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.stopRotation();
  }

  override updated(changed: Map<string, unknown>): void {
    // Restart on paused/target/prompts change. Target is the most common
    // first-render race: parent's @query('textarea') returns null on the
    // initial template build (shadow DOM not committed yet), so this
    // component mounts with target=null. The PARENT then commits, on
    // its second render passes the now-resolved textarea, and we land
    // here with `changed.has('target')` — at which point we can finally
    // start the rotation.
    if (
      changed.has('paused') ||
      changed.has('target') ||
      changed.has('prompts')
    ) {
      if (this.paused || !this.target || this.prompts.length === 0) {
        this.stopRotation();
      } else if (this.timer === null) {
        // Only start if not already running — guards against duplicate
        // timers when multiple watched props update on the same tick.
        this.startRotation();
      }
    }
  }

  /**
   * Public API — start the rotation cycle. Idempotent (no-op if already
   * running). Parent rarely needs to call this directly; controlled via
   * the `paused` property instead.
   */
  startRotation(): void {
    if (this.timer !== null) return;
    this.aborted = false;
    this.rotateNext();
  }

  /** Public API — abort rotation immediately. */
  stopRotation(): void {
    this.aborted = true;
    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /** Public API — type a single string into target with the same per-char
   *  cadence as the rotation, then halt (no erase, no next prompt). Used
   *  by the parent for chip-click inserts where the prompt should appear
   *  with a typewriter animation instead of an instant fill.
   *
   *  Stops any ongoing rotation first, so it's safe to call mid-cycle.
   *  `_currentTarget` is set to the full text immediately, so a parent
   *  intercepting a mousedown/focus during playback can read the full
   *  string and snap to it. */
  playOnce(text: string, done?: () => void): void {
    this.stopRotation();
    if (!this.target) {
      done?.();
      return;
    }
    this.aborted = false;
    this._currentTarget = text;
    this.typeText(text, () => {
      // Don't trigger the rotation cycle — the chip prompt stays put.
      done?.();
    });
  }

  private rotateNext(): void {
    if (this.aborted || !this.target || this.prompts.length === 0) return;
    const prompt = this.prompts[this.currentIndex] ?? '';
    this.currentIndex = (this.currentIndex + 1) % this.prompts.length;
    this._currentTarget = prompt;
    this.typeText(prompt, () => {
      // Hold, then clear, then pause, then next.
      this.scheduleNext(HOLD_MS, () => {
        this.clearText(() => {
          this._currentTarget = '';
          this.scheduleNext(PAUSE_AFTER_CLEAR_MS, () => this.rotateNext());
        });
      });
    });
  }

  private typeText(text: string, done: () => void): void {
    if (this.aborted || !this.target) return;
    let i = 0;
    const target = this.target;
    target.value = '';
    const tick = (): void => {
      if (this.aborted) return;
      if (i < text.length) {
        target.value = text.slice(0, i + 1);
        target.dispatchEvent(new Event('input', { bubbles: true }));
        i++;
        const delay = TYPE_BASE_MS + Math.random() * TYPE_JITTER_MS;
        this.timer = window.setTimeout(tick, delay);
      } else {
        done();
      }
    };
    tick();
  }

  private clearText(done: () => void): void {
    if (this.aborted || !this.target) return;
    const target = this.target;
    const tick = (): void => {
      if (this.aborted) return;
      const current = target.value;
      if (current.length > 0) {
        target.value = current.slice(0, Math.max(0, current.length - CLEAR_STEP_CHARS));
        target.dispatchEvent(new Event('input', { bubbles: true }));
        this.timer = window.setTimeout(tick, CLEAR_STEP_MS);
      } else {
        done();
      }
    };
    tick();
  }

  private scheduleNext(delay: number, fn: () => void): void {
    if (this.aborted) return;
    this.timer = window.setTimeout(() => {
      if (!this.aborted) fn();
    }, delay);
  }

  override render() {
    // Headless component — no visible markup. The visual output is the
    // text that appears in the parent's textarea.
    return html``;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'emma-chat-typing-prompt': EmmaChatTypingPrompt;
  }
}
